import { Icon } from '@eleo-rh/core-ui'
import { toast } from '@eleo-rh/core-ui/contexts'
import { Progress } from 'antd'
import BinaryDownloadLink from 'components/Binary/BinaryDownloadLink'
import { API_URL_ASYNC_EXPORT } from 'constants/api'
import { useAuth } from 'context/AuthProvider'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useMercure } from 'hooks/mercure'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type ExportTaskStatusEvent = {
  type: 'export_task_status_update'
  status: 'started' | 'inProgress' | 'failed' | 'done'
  processed?: number
  total?: number
  message?: string
  exportTaskId: number
  elementId?: number | null
  exportType: string
  exportName?: string | null
  documentName?: string | null
  exportConfigurable?: string | null
}

type AsyncNotificationEvent =
  | {
      type: 'retroactive_notifications'
      notificationType: 'success' | 'info' | 'warning' | 'error'
      key: string
      message: string
      description: string
      percent: number | null
    }
  | ExportTaskStatusEvent

type AsyncNotificationContextValue = {
  exportStatuses: Record<string, ExportTaskStatusEvent | undefined>
  resetExportStatus: (exportKey: string, exportTaskId?: number) => void
}

export function exportStatusKey(exportKey: string, elementId?: number | string | null) {
  return elementId === undefined || elementId === null ? exportKey : `${exportKey}:${elementId}`
}

const READY_NOTIFICATION_DURATION = 8

const AsyncNotificationContext = createContext<AsyncNotificationContextValue | undefined>(undefined)

function AsyncNotificationProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()
  const { i18n } = useI18n()
  const { profile } = useProfile()
  const { subscribe } = useMercure()
  const [exportStatuses, setExportStatuses] = useState<Record<string, ExportTaskStatusEvent | undefined>>({})
  const dismissedExportTasks = useRef(new Set<number>())

  const resetExportStatus = useCallback((exportKey: string, exportTaskId?: number) => {
    setExportStatuses(current => {
      if (exportTaskId !== undefined && current[exportKey]?.exportTaskId !== exportTaskId) {
        return current
      }

      const next = { ...current }
      delete next[exportKey]
      return next
    })
  }, [])

  useEffect(() => {
    if (!user) return

    const channels = ['/users', `/users/${user.id}`, ...(profile?.id ? [`/profiles/${profile.id}`] : [])]

    const eventSource = subscribe(channels, ['/notifications'], ({ data }: { data: AsyncNotificationEvent }) => {
      switch (data.type) {
        case 'retroactive_notifications': {
          const description = (
            <>
              {/* biome-ignore lint/security/noDangerouslySetInnerHtml: notification descriptions are rendered as trusted server-provided rich text. */}
              <div dangerouslySetInnerHTML={{ __html: data.description }}></div>
              {data.percent !== null && <Progress percent={data.percent} />}
            </>
          )

          toast[data.notificationType]({
            id: data.key,
            title: data.message,
            description,
            duration: Number.POSITIVE_INFINITY,
          })
          break
        }
        case 'export_task_status_update': {
          const exportConfigurable = data.exportConfigurable
          const statusKey = exportStatusKey(exportConfigurable ?? data.exportType, data.elementId)
          const toastId = `export-task-${data.exportTaskId}`
          setExportStatuses(current => ({
            ...current,
            [statusKey]: data,
          }))

          const pending = data.status === 'started' || data.status === 'inProgress'

          if (pending && dismissedExportTasks.current.has(data.exportTaskId)) break

          dismissedExportTasks.current.delete(data.exportTaskId)
          const description = (
            <div className="flex flex-col gap-3">
              <div>
                {data.status === 'failed'
                  ? i18n('notification.export.error_description', { error: data.message ?? i18n('internal.error') })
                  : data.status === 'done'
                    ? i18n('notification.export.description', {
                        documentName: data.exportName ?? data.documentName ?? '',
                      })
                    : i18n('notification.description.treatment_in_progress')}
              </div>
              {pending && (
                <Progress
                  percent={data.total ? Math.min(100, Math.floor((100 * (data.processed ?? 0)) / data.total)) : 0}
                />
              )}
              {data.status === 'done' && (
                <BinaryDownloadLink
                  href={API_URL_ASYNC_EXPORT.replace('ID', String(data.exportTaskId))}
                  callback={() => {
                    resetExportStatus(statusKey, data.exportTaskId)
                    toast.dismiss(toastId)
                  }}
                  buttonType="tertiary"
                  status="success"
                  size="middle"
                  icon={<Icon name="arrow-down-to-bracket" />}
                >
                  {i18n('label.download')}
                </BinaryDownloadLink>
              )}
            </div>
          )

          toast[data.status === 'failed' ? 'error' : data.status === 'done' ? 'success' : 'info']({
            id: toastId,
            position: 'bottom-right',
            icon: 'cloud-arrow-up',
            color: data.status === 'failed' ? 'red' : 'white',
            title: data.exportName
              ? `${i18n('notification.export.title')} — ${data.exportName}`
              : i18n('notification.export.title'),
            description,
            duration: data.status === 'done' ? READY_NOTIFICATION_DURATION : Number.POSITIVE_INFINITY,
            onClose: () => dismissedExportTasks.current.add(data.exportTaskId),
          })
          break
        }
      }
    })

    return () => eventSource?.close()
  }, [i18n, profile?.id, resetExportStatus, subscribe, user])

  const value = useMemo(() => ({ exportStatuses, resetExportStatus }), [exportStatuses, resetExportStatus])

  return <AsyncNotificationContext.Provider value={value}>{children}</AsyncNotificationContext.Provider>
}

export const useAsyncNotification = () => {
  const context = useContext(AsyncNotificationContext)

  if (!context) {
    throw new Error('useAsyncNotification must be used within an AsyncNotificationProvider')
  }

  return context
}

export default AsyncNotificationProvider
